import React, { useEffect, useRef, useState } from 'react';
import { translations } from '../translations';

const SEND_EMAIL_URL = 'https://joaosilva.ch/sendemail.php';
const RECAPTCHA_SITE_KEY = '6LdvkEstAAAAANZNmTrS1JCs5RAhPKu8MRZFaen_'; // Recaptcha enterprise key

const initialForm = { name: '', email: '', subject: '', message: '', website: '' };

function Postmark({ stamped = false, className = '' }) {
  return (
    <svg
      viewBox="0 0 180 140"
      className={`postmark ${stamped ? 'postmark--stamped' : ''} ${className}`}
      aria-hidden="true"
    >
      <g transform="rotate(-10 90 70)">
        <circle cx="70" cy="70" r="52" className="postmark-ring-outer" />
        <circle cx="70" cy="70" r="40" className="postmark-ring-inner" />
        {stamped && (
          <path
            className="postmark-check"
            d="M52 70 L65 84 L90 54"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )}
        {[0, 1, 2, 3, 4].map((i) => (
          <line
            key={i}
            x1={128}
            y1={44 + i * 13}
            x2={168}
            y2={38 + i * 13}
            className="postmark-cancel-line"
          />
        ))}
      </g>
    </svg>
  );
}

function loadRecaptchaV3(siteKey) {
  if (globalThis.__recaptchaV3Ready) {
    return globalThis.__recaptchaV3Ready;
  }

  globalThis.__recaptchaV3Ready = new Promise((resolve) => {
    const existing = document.getElementById('recaptcha-v3-script');
    if (existing) {
      const check = setInterval(() => {
        if (globalThis.grecaptcha) {
          clearInterval(check);
          globalThis.grecaptcha.ready(() => resolve());
        }
      }, 50);
      return;
    }

    const script = document.createElement('script');
    script.id = 'recaptcha-v3-script';
    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      globalThis.grecaptcha.ready(() => resolve());
    };
    document.head.appendChild(script);
  });

  return globalThis.__recaptchaV3Ready;
}

function Contact({ language }) {
  const t = translations[language].contact;

  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState({ state: 'idle', message: '' });
  const startedAtRef = useRef(Date.now());

  useEffect(() => {
    startedAtRef.current = Date.now();

    let cancelled = false;

    loadRecaptchaV3(RECAPTCHA_SITE_KEY).then(() => {
      if (cancelled) return;
      const badge = document.querySelector('.grecaptcha-badge');
      if (badge) badge.style.visibility = 'visible';
    });

    return () => {
      cancelled = true;
      const badge = document.querySelector('.grecaptcha-badge');
      if (badge) badge.style.visibility = 'hidden';
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ state: 'submitting', message: '' });

    try {
      await loadRecaptchaV3(RECAPTCHA_SITE_KEY);

      const recaptchaToken = await globalThis.grecaptcha.execute(RECAPTCHA_SITE_KEY, {
        action: 'contact_submit',
      });

      if (!recaptchaToken) {
        setStatus({ state: 'error', message: t.errors.captchaLoad || 'Captcha verification failed. Please try again.' });
        return;
      }

      const res = await fetch(SEND_EMAIL_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          subject: form.subject,
          message: form.message,
          website: form.website,
          startedAt: startedAtRef.current,
          recaptchaToken,
          recaptchaAction: 'contact_submit',
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data.success) {
        setStatus({ state: 'error', message: data.message || t.errors.generic });
        return;
      }

      setStatus({ state: 'success', message: t.success });
      setForm(initialForm);
    } catch (err) {
      setStatus({ state: 'error', message: t.errors.network, code: err });
    }
  };

  const isSubmitting = status.state === 'submitting';

  return (
    <div className="page contact-page">
      <div className="contact-shell">
        <header className="contact-intro">
          <span className="contact-eyebrow">{t.eyebrow}</span>
          <h1 className="contact-title">{t.title}</h1>
          <p className="contact-description">{t.description}</p>
          <Postmark className="contact-intro-mark" />
        </header>

        <div className="contact-divider" aria-hidden="true" />

        <div className="contact-card">
          <form className="contact-form" onSubmit={handleSubmit} noValidate>
            <div className="contact-row">
              <div className="contact-field">
                <label htmlFor="contact-name">{t.fields.name}</label>
                <input
                  id="contact-name"
                  name="name"
                  type="text"
                  autoComplete="name"
                  required
                  maxLength={100}
                  value={form.name}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
              </div>

              <div className="contact-field">
                <label htmlFor="contact-email">{t.fields.email}</label>
                <input
                  id="contact-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={form.email}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="contact-field">
              <label htmlFor="contact-subject">{t.fields.subject}</label>
              <input
                id="contact-subject"
                name="subject"
                type="text"
                required
                maxLength={150}
                value={form.subject}
                onChange={handleChange}
                disabled={isSubmitting}
              />
            </div>

            <div className="contact-field">
              <label htmlFor="contact-message">{t.fields.message}</label>
              <textarea
                id="contact-message"
                name="message"
                rows={5}
                required
                maxLength={5000}
                value={form.message}
                onChange={handleChange}
                disabled={isSubmitting}
              />
            </div>

            {/* Honeypot: real users never see or fill this in */}
            <div className="contact-honeypot" aria-hidden="true">
              <label htmlFor="contact-website">Website</label>
              <input
                id="contact-website"
                name="website"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={form.website}
                onChange={handleChange}
              />
            </div>

            <div className="contact-submit-row">
              <button className="contact-seal" type="submit" disabled={isSubmitting}>
                <svg viewBox="0 0 24 24" className="contact-seal-icon" aria-hidden="true">
                  <path
                    d="M3 5l18 7-18 7 4-7-4-7z"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>{isSubmitting ? t.sending : t.send}</span>
              </button>

              {status.state === 'success' && (
                <output className="contact-status contact-status--success">
                  <Postmark stamped className="contact-status-mark" />
                  {status.message}
                </output>
              )}
              {status.state === 'error' && (
                <p className="contact-status contact-status--error" role="alert">
                  {status.message}
                </p>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default Contact;